import { Suspense, useEffect, type ReactNode } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import SiteNav from './components/Nav';
import SiteFooter from './components/Footer';
import SignInScreen from './components/SignInScreen';
import { Spinner } from './components/ui';
import { AUTH_CALLBACK_PATH, useAuth } from './auth';
import { lazyGroup, lazyPage, type LazyPage } from './lazyPage';
import { AGENT_DOCS } from './content/agentDocs';

// Each page is its own chunk (lazyPage.tsx). The dashboard is one: its
// layout and the pages in its <Outlet /> are shown together.
const HomePage = lazyPage(() => import('./pages/Home').then((m) => m.default));
const TermsPage = lazyPage(() => import('./pages/Legal').then((m) => m.TermsPage));
const PrivacyPage = lazyPage(() => import('./pages/Legal').then((m) => m.PrivacyPage));
const LogoPage = lazyPage(() => import('./pages/logo/LogoPage').then((m) => m.default));
const ClaimPage = lazyPage(() => import('./pages/Claim').then((m) => m.default));
const OAuthAuthorizePage = lazyPage(() => import('./pages/OAuthAuthorize').then((m) => m.default));
const OAuthLogoutPage = lazyPage(() => import('./pages/OAuthLogout').then((m) => m.default));
const StatusPage = lazyPage(() => import('./pages/Status').then((m) => m.default));
const SocialsPage = lazyPage(() => import('./pages/Socials').then((m) => m.default));
const HelpPage = lazyPage(() => import('./pages/Help').then((m) => m.default));
const DocPage = lazyPage(() => import('./pages/Doc').then((m) => m.default));
const AdminPage = lazyPage(() => import('./pages/Admin').then((m) => m.default));
const dashboardPage = lazyGroup(() => import('./pages/dashboard'));
const DashboardLayout = dashboardPage((m) => m.DashboardLayout);
const DashboardHome = dashboardPage((m) => m.DashboardHome);
const ProjectLayout = dashboardPage((m) => m.ProjectLayout);
const ProjectOverview = dashboardPage((m) => m.ProjectOverview);
const ProjectBuilds = dashboardPage((m) => m.ProjectBuilds);
const ProjectImages = dashboardPage((m) => m.ProjectImages);

/** The prerendered pages (scripts/prerender.mjs), by path: their chunk has
 *  to be in before the page is rendered to HTML (entry-server.tsx) and
 *  before that HTML is hydrated (main.tsx), so neither meets a page still
 *  loading. */
export const PRERENDERED_PAGES: Record<string, LazyPage> = {
  '/': HomePage,
  '/terms': TermsPage,
  '/privacy': PrivacyPage,
  '/logo': LogoPage,
  '/help': HelpPage,
  ...Object.fromEntries(AGENT_DOCS.map((doc) => [`/${doc.name}`, DocPage])),
};

// Two shells: the marketing site's own SiteNav/SiteFooter for `/` and
// `/claim`, and a separate full-height sidebar shell (DashboardLayout) for
// everything under `/:alias/*` — a key's own dashboard is a different kind
// of page, not a marketing page with extra content bolted on.
/** Starts every page change at the top, like a normal page load (the
 *  router keeps the old scroll position otherwise, so a footer link would
 *  open its page at the bottom). Links to a #section are left alone. */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo({ top: 0 });
  }, [pathname, hash]);
  return null;
}

/** The marketing site's own pages: SiteNav and SiteFooter around the page,
 *  which holds its place while its chunk loads rather than pulling the
 *  footer up under the nav. */
function SitePage({ children }: { children: ReactNode }) {
  return (
    <div className="site-main">
      <SiteNav />
      <Suspense fallback={<div className="page-loading" />}>{children}</Suspense>
      <SiteFooter />
    </div>
  );
}

/** Auth0's return (AUTH_CALLBACK_PATH): AuthProvider finishes the sign-in
 *  and sends the visitor on to where it started, so all this shows is a
 *  moment's "Signing you in…". Reached any other way (no sign-in to finish,
 *  or Auth0 off), there's nothing to wait for: home. */
function AuthCallback() {
  const { loading } = useAuth();
  if (!loading) return <Navigate to="/" replace />;
  return (
    <SignInScreen>
      <Spinner label="Signing you in…" />
    </SignInScreen>
  );
}

/** /offline → /status, same query (the donation round trip included). */
function ToStatus() {
  const { search } = useLocation();
  return <Navigate to={`/status${search}`} replace />;
}

export default function App() {
  return (
    <>
    <ScrollToTop />
    {/* The chrome-free pages' own wait for their chunk: SitePage holds its
        own, under the nav. Blank, like the page before any script ran. */}
    <Suspense fallback={null}>
    <Routes>
      <Route path="/" element={<SitePage><HomePage /></SitePage>} />
      <Route path="/terms" element={<SitePage><TermsPage /></SitePage>} />
      <Route path="/privacy" element={<SitePage><PrivacyPage /></SitePage>} />
      <Route path="/claim" element={<ClaimPage />} />
      {/* Chrome-free on purpose: a dedicated "this site is starting or
          offline" page, not part of the marketing site (see pages/Status.tsx).
          /offline was its old address — the control plane's redirect, and
          card checkouts started before the move — and forwards there. */}
      <Route path="/status" element={<StatusPage />} />
      <Route path="/offline" element={<ToStatus />} />
      {/* The sign-in path, chrome-free too (components/SignInScreen.tsx).
          AUTH_CALLBACK_PATH has two segments under `auth`, a reserved alias,
          so it can't shadow anyone's dashboard. */}
      <Route path={AUTH_CALLBACK_PATH} element={<AuthCallback />} />
      <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />
      <Route path="/oauth/logout" element={<OAuthLogoutPage />} />
      {/* Social-media artwork, chrome-free; unlinked and noindex'd (see
          pages/Socials.tsx). A static route, so it outranks /:alias. */}
      <Route path="/socials" element={<SocialsPage />} />
      <Route path="/logo" element={<SitePage><LogoPage /></SitePage>} />
      <Route path="/help" element={<SitePage><HelpPage /></SitePage>} />
      <Route path="/admin" element={<SitePage><AdminPage /></SitePage>} />
      {/* The agent documents (pages/Doc.tsx): static routes, so they
          outrank /:alias. */}
      {AGENT_DOCS.map((doc) => (
        <Route key={doc.name} path={`/${doc.name}`} element={<SitePage><DocPage /></SitePage>} />
      ))}
      <Route path="/:alias" element={<DashboardLayout />}>
        <Route index element={<DashboardHome />} />
        <Route path=":project" element={<ProjectLayout />}>
          <Route index element={<ProjectOverview />} />
          <Route path="builds" element={<ProjectBuilds />} />
          <Route path="images" element={<ProjectImages />} />
        </Route>
      </Route>
    </Routes>
    </Suspense>
    </>
  );
}
